import { useCallback, useEffect, useRef, useState } from "react"
import { useUnit } from "effector-react"
import { Dayjs } from "utils/dayjs"

import shippingOrderCartStore from "stores/shippingOrderCart"
import { Guid } from "@/types"
import { ErrorCode, UserPermission } from "config/constants"
import CustomError from "models/CustomError"
import toastMessages from "stores/toastMessages"
import { useTenantSettings } from "stores/tenantSettings"
import { ShippingOrderType } from "models/ShippingOrder"
import { usePermissions } from "stores/permissions"
import ShippingOrderCart from "models/ShippingOrderCart"

interface IUseShippingOrderCartParams {
    reservationId?: Guid
    shippingOrderId?: Guid
    type?: ShippingOrderType
    onInitialized?(params: { addAssets: (assetsToAdd: Guid[]) => Promise<ShippingOrderCart> }): Promise<void> | void
}

export default function useShippingOrderCart({
    reservationId,
    shippingOrderId,
    type,
    onInitialized,
}: IUseShippingOrderCartParams) {
    const cart = useUnit(shippingOrderCartStore.$cart)
    const { hasPermission } = usePermissions()
    const [errorCode, setErrorCode] = useState<ErrorCode | null>(null)
    const [errorEmail, setErrorEmail] = useState<string>("")
    const [errorDate, setErrorDate] = useState<Dayjs | null>(null)
    const [overrideOwnership, setOverrideOwnership] = useState(false)
    const [isInitCartPending, setIsInitCartPending] = useState(false)
    const [initialForwawrdToShipping, setInitialForwardToShipping] = useState(false)
    const initCartInFlightRef = useRef<Promise<void> | null>(null)
    const { deliveryTerms, fetchDeliveryTerms, tenantWarehouses, fetchTenantWarehouses } = useTenantSettings()

    const initCart = useCallback(async () => {
        if (initCartInFlightRef.current) {
            return initCartInFlightRef.current
        }

        const request = (async () => {
            try {
                const newCart = await shippingOrderCartStore.createCart({
                    reservationId,
                    type,
                    shippingOrderId,
                    overrideOwnership,
                })

                if (newCart.linkAddAssets) {
                    onInitialized?.({ addAssets: shippingOrderCartStore.addAssets })
                }
                resetError()
                setInitialForwardToShipping(newCart.forwardToShipping)
            } catch (e) {
                if (e instanceof CustomError) {
                    const error: CustomError<{ email: string, date: Dayjs | null, readOnly?: boolean } | undefined> = e

                    setErrorCode(error.code || null)
                    setErrorEmail(error.payload?.email || "")
                    setErrorDate(error.payload?.date || null)
                } else {
                    toastMessages.showSystemError()
                }
            }
        })()

        initCartInFlightRef.current = request

        try {
            await request
        } finally {
            if (initCartInFlightRef.current === request) {
                initCartInFlightRef.current = null
            }
        }
    }, [onInitialized, overrideOwnership, reservationId, shippingOrderId, type])

    const initDeliveryTerms = useCallback(async () => {
        try {
            await fetchDeliveryTerms()
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [fetchDeliveryTerms])

    const initTenantWarehouses = useCallback(async () => {
        try {
            await fetchTenantWarehouses()
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [fetchTenantWarehouses])

    useEffect(() => {
        (async () => {
            setIsInitCartPending(true)
            await Promise.all([
                initCart(),
                initDeliveryTerms(),
                initTenantWarehouses(),
            ])
            setIsInitCartPending(false)
        })()
    }, [initCart, initDeliveryTerms, initTenantWarehouses])

    function resetError() {
        setErrorCode(null)
        setErrorEmail("")
        setErrorDate(null)
    }

    async function resetSessionTimeout() {
        try {
            await shippingOrderCartStore.touchCart()
        } catch (e) {
            toastMessages.showSystemError()
        }
    }

    const isCartBusy = useUnit(shippingOrderCartStore.$cartUpdatesInFlightCount) > 0

    return {
        cart,
        initialReadyForShipping: initialForwawrdToShipping,
        initCart,
        isCartBusy,
        showContinueSessionModal: useUnit(shippingOrderCartStore.$showContinueSessionModal),
        updateCart: shippingOrderCartStore.updateCart,
        touchCart: shippingOrderCartStore.touchCart,
        deleteCart: shippingOrderCartStore.deleteCart,
        publishCart: shippingOrderCartStore.publishCart,
        changeCartStatus: cart?.linkChangeStatus ? shippingOrderCartStore.changeCartStatus : undefined,
        addAssets: cart?.linkAddAssets ? shippingOrderCartStore.addAssets : undefined,
        changeAssets: cart?.linkUpdateAsset ? shippingOrderCartStore.changeAssets : undefined,
        deleteAssets: cart?.linkDeleteAssets ? shippingOrderCartStore.deleteAssets : undefined,
        createManualPositions: hasPermission(UserPermission.LoadingListsEdit) && cart?.linkCreateManualPositions ? shippingOrderCartStore.createManualPositions : undefined,
        createManualPositionsWithLink: shippingOrderCartStore.createManualPositions,
        updateManualPositions: cart?.linkUpdateManualPosition ? shippingOrderCartStore.updateManualPositions : undefined,
        deleteManualPosition: cart?.linkDeleteManualPosition ? shippingOrderCartStore.deleteManualPosition : undefined,
        createTransport: cart?.linkCreateTransport ? shippingOrderCartStore.createTransport : undefined,
        updateTransport: cart?.linkUpdateTransport ? shippingOrderCartStore.updateTransport : undefined,
        deleteTransport: cart?.linkDeleteTransport ? shippingOrderCartStore.deleteTransport : undefined,
        updateSegment: cart?.linkUpdateSegment ? shippingOrderCartStore.updateSegment : undefined,
        moveSegment: cart?.linkUpdateAsset && cart?.linkUpdateManualPosition ? shippingOrderCartStore.moveSegment : undefined,
        importLoadingList: cart?.linkImportLoadingList ? shippingOrderCartStore.importLoadingList : undefined,
        exportLoadingList: cart?.linkExportLoadingList ? shippingOrderCartStore.exportLoadingList : undefined,
        downloadPickingList: cart?.linkGetPickingListReport ? shippingOrderCartStore.downloadPickingList : undefined,
        getRequestLocations: shippingOrderCartStore.getRequestLocations,
        createRequestLocation: shippingOrderCartStore.createRequestLocation,
        reset: shippingOrderCartStore.reset,
        resetSessionTimeout,
        errorCode,
        errorEmail,
        errorDate,
        setOverrideOwnership,
        isInitCartPending,
        deliveryTerms,
        tenantWarehouses,
    }
}
